import React, { useEffect, useState, useMemo } from 'react';
import { useWebhookDeliveries } from '../hooks/useWebhookDeliveries';

interface WebhookMonitorProps {
  playbookId: string;
}

const WebhookMonitor: React.FC<WebhookMonitorProps> = ({ playbookId }) => {
  const { deliveries, total, loading, error, retrying, load, retry } = useWebhookDeliveries(playbookId);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    load({ status: statusFilter || undefined, limit: 50 });
  }, [load, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    const delivered = deliveries.filter((d) => d.status === 'delivered').length;
    const failed = deliveries.filter((d) => d.status === 'failed').length;
    const retryingCount = deliveries.filter((d) => d.status === 'retrying').length;
    const pending = deliveries.filter((d) => d.status === 'pending').length;
    return { delivered, failed, retrying: retryingCount, pending, total: deliveries.length };
  }, [deliveries]);

  // Per-URL health
  const urlHealth = useMemo(() => {
    const map = new Map<string, { total: number; succeeded: number }>();
    for (const d of deliveries) {
      const entry = map.get(d.url) || { total: 0, succeeded: 0 };
      entry.total++;
      if (d.status === 'delivered') entry.succeeded++;
      map.set(d.url, entry);
    }
    return Array.from(map.entries()).map(([url, h]) => ({
      url,
      total: h.total,
      succeeded: h.succeeded,
      rate: h.total > 0 ? h.succeeded / h.total : 0,
    }));
  }, [deliveries]);

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Delivered" value={stats.delivered} color="emerald" />
        <StatCard label="Failed" value={stats.failed} color="rose" />
        <StatCard label="Retrying" value={stats.retrying} color="amber" />
        <StatCard
          label="Success Rate"
          value={stats.total > 0 ? `${Math.round((stats.delivered / stats.total) * 100)}%` : '—'}
          color={stats.total > 0 && stats.delivered / stats.total >= 0.9 ? 'emerald' : 'amber'}
        />
      </div>

      {/* Per-URL health */}
      {urlHealth.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Per-URL Health</h4>
          <div className="space-y-1">
            {urlHealth.map((u) => (
              <div key={u.url} className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${
                  u.rate >= 0.9 ? 'bg-emerald-500' : u.rate >= 0.7 ? 'bg-amber-500' : 'bg-rose-500'
                }`} />
                <span className="text-[10px] text-gray-700 dark:text-gray-300 truncate flex-1 font-mono">{u.url}</span>
                <span className={`text-[10px] font-medium ${
                  u.rate >= 0.9 ? 'text-emerald-600 dark:text-emerald-400' : u.rate >= 0.7 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'
                }`}>
                  {Math.round(u.rate * 100)}%
                </span>
                <span className="text-[10px] text-gray-400">({u.total})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
        >
          <option value="">All statuses</option>
          <option value="delivered">Delivered</option>
          <option value="failed">Failed</option>
          <option value="retrying">Retrying</option>
          <option value="pending">Pending</option>
        </select>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">{total} total</span>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading deliveries...</span>
        </div>
      )}

      {error && (
        <div className="text-sm text-rose-600 dark:text-rose-400">{error}</div>
      )}

      {/* Deliveries table */}
      {!loading && deliveries.length > 0 && (
        <div className="space-y-1">
          {deliveries.map((d) => (
            <div key={d.id} className={`rounded-lg border overflow-hidden ${
              d.status === 'failed' ? 'border-rose-200 bg-rose-50/30 dark:border-rose-800 dark:bg-rose-900/10' : 'border-gray-200 dark:border-gray-700'
            }`}>
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
              >
                <StatusDot status={d.status} />
                <span className="text-[10px] text-gray-500 dark:text-gray-400 w-28">
                  {new Date(d.created_at).toLocaleString()}
                </span>
                <span className="text-[10px] font-mono text-gray-700 dark:text-gray-300 truncate flex-1">{d.url}</span>
                {d.response_status && (
                  <span className={`text-[10px] font-medium ${
                    d.response_status < 400 ? 'text-emerald-600' : 'text-rose-600'
                  }`}>
                    {d.response_status}
                  </span>
                )}
                <span className="text-[10px] text-gray-400">#{d.attempt_number}</span>
                {d.status === 'failed' && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); retry(d.id); }}
                    disabled={retrying[d.id]}
                    className="rounded border border-rose-300 px-2 py-0.5 text-[10px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-600 dark:text-rose-400"
                  >
                    {retrying[d.id] ? 'Retrying...' : 'Retry'}
                  </button>
                )}
              </div>
              {expandedId === d.id && (
                <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-700 space-y-2 text-[10px]" style={{ animation: 'coverageDrillDown 200ms ease-out' }}>
                  <div>
                    <span className="font-medium text-gray-500 dark:text-gray-400">Method:</span>{' '}
                    <span className="text-gray-700 dark:text-gray-300">{d.method}</span>
                  </div>
                  {d.request_headers && Object.keys(d.request_headers).length > 0 && (
                    <div>
                      <span className="font-medium text-gray-500 dark:text-gray-400">Request Headers:</span>
                      <pre className="mt-0.5 rounded bg-gray-100 dark:bg-gray-700 p-1.5 font-mono text-[9px] overflow-x-auto">
                        {JSON.stringify(d.request_headers, null, 2).slice(0, 500)}
                      </pre>
                    </div>
                  )}
                  {!!d.request_body && (
                    <div>
                      <span className="font-medium text-gray-500 dark:text-gray-400">Request Body:</span>
                      <pre className="mt-0.5 rounded bg-gray-100 dark:bg-gray-700 p-1.5 font-mono text-[9px] overflow-x-auto">
                        {JSON.stringify(d.request_body, null, 2).slice(0, 500)}
                      </pre>
                    </div>
                  )}
                  {d.response_body && (
                    <div>
                      <span className="font-medium text-gray-500 dark:text-gray-400">Response Body:</span>
                      <pre className="mt-0.5 rounded bg-gray-100 dark:bg-gray-700 p-1.5 font-mono text-[9px] overflow-x-auto">
                        {d.response_body.slice(0, 500)}
                      </pre>
                    </div>
                  )}
                  {d.error_message && (
                    <div className="text-rose-600 dark:text-rose-400">
                      <span className="font-medium">Error:</span> {d.error_message}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && deliveries.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No webhook deliveries found.</p>
      )}
    </div>
  );
};

function StatusDot({ status }: { status: string }) {
  const color = status === 'delivered' ? 'bg-emerald-500' : status === 'failed' ? 'bg-rose-500' : status === 'retrying' ? 'bg-amber-500' : 'bg-gray-400';
  return <span className={`h-2 w-2 rounded-full ${color} shrink-0`} />;
}

function StatCard({ label, value, color }: { label: string; value: string | number; color?: 'emerald' | 'amber' | 'rose' }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-center">
      <div className={`text-xl font-bold ${
        color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' :
        color === 'amber' ? 'text-amber-600 dark:text-amber-400' :
        color === 'rose' ? 'text-rose-600 dark:text-rose-400' :
        'text-gray-900 dark:text-white'
      }`}>{value}</div>
      <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{label}</div>
    </div>
  );
}

export default WebhookMonitor;
